// Birim varyantı DR4/DR6/DR8 — IndicatorModal birim akışının saf/zamanlayıcı çekirdeği (DOM yok;
// bileşen ince kalır, testler bunu doğrudan sürer). Akış: seçim değişir → anında "Etki
// hesaplanıyor…" → 300 ms debounce → `previewKpiUnitChange` (son istek kazanır, bayat yanıt atılır)
// → `UnitChangeBand` + dinamik Kaydet ("Kaydet ve N değeri çevir", `canSave`) → kullanım varsa
// tek cümle teyit → `applyKpiUnitChange` (diğer alanların PATCH'inden AYRI). 409 yalnız yarışta
// görülür: ileti bant altında + "Yeniden kontrol et" (preview yeniden).

import {
  normalizeUnitKey,
  type KpiUnitChangeBody,
  type KpiUnitChangePreview,
  type KpiUnitChangeResult,
} from "@mahir/shared";
import { ApiError } from "../api/client";
import { applyKpiUnitChange, previewKpiUnitChange } from "../api/kpi";
import { canSave, needsUnitMode, type UnitChangeMode } from "./kpi-unit-band";
import { KPI_UNIT_COPY } from "./kpi-unit-copy";

export const UNIT_PREVIEW_DEBOUNCE_MS = 300;

export interface UnitPreviewState {
  loading: boolean;
  error: boolean;
  preview: KpiUnitChangePreview | null;
}
export const IDLE_UNIT_PREVIEW: UnitPreviewState = { loading: false, error: false, preview: null };

/** Müşteri kaydı karşılaştırması: null = katalog varsayılanı; birim yazımları normalize edilir. */
export function sameUnitPick(a: string | null, b: string | null): boolean {
  const ka = a === null || a.trim() === "" ? null : normalizeUnitKey(a);
  const kb = b === null || b.trim() === "" ? null : normalizeUnitKey(b);
  return ka === kb;
}

/**
 * Seçim → müşteri kaydı: katalog varsayılanına eşit birim ("Diğer birim…" ile yazılmış olsa da)
 * varsayılan seçimi (null) sayılır — varsayılanla aynı müşteri kaydı yazılmaz.
 */
export function normalizeUnitPick(unit: string | null, defaultUnit: string | null): string | null {
  if (unit === null || unit.trim() === "") return null;
  return defaultUnit !== null && sameUnitPick(unit, defaultUnit) ? null : unit;
}

export interface UnitPreviewScheduler {
  /** seçim değişti — anında yükleniyor, 300 ms sonra istek (öncekini iptal eder) */
  schedule: (unit: string | null) => void;
  /** "Yeniden kontrol et / yeniden dene" — beklemeden istek */
  refresh: (unit: string | null) => void;
  /** seçim geri alındı / bileşen kapandı — bekleyen ve uçuştaki yanıtlar atılır */
  cancel: () => void;
}

export function createUnitPreviewScheduler(
  target: { clientId: string; metricId: string },
  onState: (s: UnitPreviewState) => void,
  delayMs: number = UNIT_PREVIEW_DEBOUNCE_MS,
): UnitPreviewScheduler {
  let seq = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const run = (unit: string | null, my: number): void => {
    previewKpiUnitChange({ clientId: target.clientId, metricId: target.metricId, unit }).then(
      (preview) => {
        if (my === seq) onState({ loading: false, error: false, preview });
      },
      () => {
        if (my === seq) onState({ loading: false, error: true, preview: null });
      },
    );
  };
  return {
    schedule(unit) {
      clear();
      const my = ++seq;
      onState({ loading: true, error: false, preview: null });
      timer = setTimeout(() => {
        timer = null;
        run(unit, my);
      }, delayMs);
    },
    refresh(unit) {
      clear();
      const my = ++seq;
      onState({ loading: true, error: false, preview: null });
      run(unit, my);
    },
    cancel() {
      clear();
      seq += 1;
      onState(IDLE_UNIT_PREVIEW);
    },
  };
}

/** "Kaydet ve N değeri çevir" — yalnız gerçek çevrimde N > 0 (yeniden etiketlemede "Kaydet"). */
export function unitSaveButton(
  state: UnitPreviewState,
  mode: UnitChangeMode | null,
  saving: boolean,
  /** 409 yarışı sonrası: "Yeniden kontrol et" koşana dek pasif */
  conflict = false,
): { label: string; disabled: boolean } {
  const p = state.preview;
  const n = p && needsUnitMode(p) && mode !== "relabel" ? p.values : 0;
  return {
    label: saving ? KPI_UNIT_COPY.saving : KPI_UNIT_COPY.save(n),
    disabled:
      saving || conflict || !canSave(p, mode, { loading: state.loading, error: state.error }),
  };
}

/** Gövde: `mode` yalnız DR8 radyosu gerektiğinde gönderilir. */
export function unitApplyBody(
  target: { clientId: string; metricId: string },
  unit: string | null,
  preview: KpiUnitChangePreview,
  mode: UnitChangeMode | null,
): KpiUnitChangeBody {
  return {
    clientId: target.clientId,
    metricId: target.metricId,
    unit,
    ...(needsUnitMode(preview) && mode ? { mode } : {}),
  };
}

/** Son tıktaki tek cümle teyit; kullanım yoksa (değer/hedef 0) teyit yok → null. */
export function unitConfirmText(
  preview: KpiUnitChangePreview,
  mode: UnitChangeMode | null,
  unitLabel: string,
): string | null {
  if (preview.noop) return null;
  const values = Math.max(preview.values, preview.reportedValues);
  if (values === 0 && preview.targets === 0) return null;
  return mode === "relabel" || !needsUnitMode(preview)
    ? KPI_UNIT_COPY.confirmRelabel(values, preview.targets, unitLabel)
    : KPI_UNIT_COPY.confirm(values, preview.targets, unitLabel);
}

/** Başarı bandı (DR6): çevrimde "N değer çevrildi", yeniden etiketlemede "birimi düzeltildi". */
export function unitSuccessMessage(
  r: { mode: UnitChangeMode; unit: string | null; converted: number; targetsConverted: number },
  label: string,
  clientName: string,
): string {
  const unit = r.unit ?? KPI_UNIT_COPY.unitless;
  const fn = r.mode === "relabel" ? KPI_UNIT_COPY.successRelabel : KPI_UNIT_COPY.success;
  return fn(label, clientName, unit, r.converted, r.targetsConverted);
}

export type UnitSubmitOutcome =
  | { ok: true; result: KpiUnitChangeResult }
  | { ok: false; conflict: boolean; message: string };

/** `applyKpiUnitChange`; 409 (yarış — kullanım/formül araya girdi) ayrı işaretlenir. */
export async function submitUnitChange(body: KpiUnitChangeBody): Promise<UnitSubmitOutcome> {
  try {
    return { ok: true, result: await applyKpiUnitChange(body) };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      return { ok: false, conflict: true, message: KPI_UNIT_COPY.conflict(err.message) };
    }
    return {
      ok: false,
      conflict: false,
      message: err instanceof Error && err.message ? err.message : KPI_UNIT_COPY.applyFailed,
    };
  }
}
